// src/components/common/UserForm.jsx
import { useRef, useState, useCallback } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { Undo2, Redo2, Plus } from "lucide-react";
import TablePresetPicker from "../userform/TablePresetPicker";
import { TABLE_PRESETS } from "../userform/tablePresets";
import GridCanvas from "../userform/GridCanvas";
import BlockRenderer from "../userform/BlockRenderer";
import { useGridBackground, useAutosave, useFormState } from "../userform/hooks";

import {
  withConstraints,
  parseMaybeJSON,
  newRow,
  todayISO,
  sanitizeTemplateBody,
  compressImage,
  getBottomY,
} from "../userform/utils";

import {
  DEFAULT_W,
  DEFAULT_H,
  DEFAULT_LAYOUT,
  DEFAULT_TABLE_COLUMNS,
} from "../userform/constants";

import FormTemplateManager from "./FormTemplateManager";
import FormContentManager from "./FormContentManager";

export default function UserForm({
  initialLayout,
  mode = 2,
  module = "material",
  formType = "PO",
  scope = "project",
  project,
}) {
  const containerRef = useRef(null);
  const editorRef = useRef(null);
  const hiddenFileRef = useRef(null);

  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false); // ✅ 프리셋 선택 모달

  // 상태 (리듀서 기반)
  const {
    state,
    setLayout,
    patchValues,
    patchBlocks,
    patchCustom,
    patchTables,
    undo,
    redo,
    toggleAlign,
    resetDirty,
  } = useFormState({
    layout: (initialLayout || DEFAULT_LAYOUT).map(withConstraints),
    blocks: {
      "block-title": { title: "제목", kind: "title", locked: false },
      "block-date": { title: "작성일", kind: "date", locked: false },
      "block-editor": { title: "본문", kind: "editor", locked: false },
      "block-photos": { title: "사진 첨부", kind: "photos", locked: false },
      "block-files": { title: "파일 첨부", kind: "files", locked: false },
      "block-note": { title: "메모", kind: "note", locked: false },
    },
    values: {
      title: "",
      dateStr: todayISO(),
      editorHtml: "<p>본문을 입력하세요.</p>",
      note: "",
      photos: [],
      files: [],
    },
  });

  const alignMode = state.alignMode;
  const gridBackgroundStyle = useGridBackground(alignMode, containerRef);

  // 자동 저장
  const getBody = useCallback(
    () => ({
      layout: state.layout,
      values: state.values,
      blocksMeta: state.blocks,
      customValues: state.customValues,
      tables: state.tables,
    }),
    [state]
  );
  useAutosave(getBody, [
    state.layout,
    state.values,
    state.blocks,
    state.customValues,
    state.tables,
  ]);

  // 템플릿 적용
  const resolveTemplateBody = async (tpl) => {
    let payload = tpl?.body_json ?? tpl?.template_snapshot ?? null;
    let body = parseMaybeJSON(payload) || payload;
    if (!body && tpl?.id) {
      try {
        const qs = project ? `?project=${encodeURIComponent(project)}` : "";
        const res = await fetch(`/api/doctemp/templates/${tpl.id}/${qs}`);
        if (res.ok) {
          const full = await res.json();
          body =
            parseMaybeJSON(full?.body_json) ||
            parseMaybeJSON(full?.template_snapshot);
        }
      } catch {}
    }
    return body;
  };

  const applyBody = useCallback(
    (raw) => {
      const body = sanitizeTemplateBody(raw);
      if (!body) return;
      setLayout((body.layout || []).map(withConstraints));
      patchValues({
        ...(body.values || {}),
        dateStr: body?.values?.dateStr || todayISO(),
      });
      if (body.blocksMeta) patchBlocks(body.blocksMeta);
      if (body.customValues) patchCustom(body.customValues);
      if (body.tables) patchTables(body.tables);
      resetDirty();
    },
    [setLayout, patchValues, patchBlocks, patchCustom, patchTables, resetDirty]
  );

  const handleTemplateSelect = async (tpl) => {
    setCurrentTemplate(tpl);
    const body = await resolveTemplateBody(tpl);
    if (body) applyBody(body);
  };

  // 파일/이미지
  const handlePickPhotos = async (e) => {
    const list = Array.from(e.target.files || []);
    const next = [...state.values.photos];
    for (const file of list) {
      const src = await compressImage(file);
      next.push({
        id: crypto.randomUUID(),
        name: file.name,
        size: file.size,
        src,
      });
    }
    patchValues({ photos: next });
    e.target.value = "";
  };

  const insertImageToEditor = (img) => {
    const ed = editorRef.current;
    if (!ed) return;
    const figure = `<figure style="margin:0 0 8px 0;"><img src="${img.src}" alt="${img.name}" style="max-width:100%;height:auto" /><figcaption style="font-size:12px;color:#64748b">${img.name}</figcaption></figure>`;
    ed.insertContent(figure);
  };

  const removePhoto = (id) =>
    patchValues({ photos: state.values.photos.filter((p) => p.id !== id) });

  const handleAttachFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    const next = [...state.values.files];
    for (const file of list) {
      const dataUrl = await new Promise((resolve) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.readAsDataURL(file);
      });
      next.push({
        id: crypto.randomUUID(),
        name: file.name,
        size: file.size,
        type: file.type || "application/octet-stream",
        dataUrl,
      });
    }
    patchValues({ files: next });
    e.target.value = "";
  };

  const removeFile = (id) =>
    patchValues({ files: state.values.files.filter((f) => f.id !== id) });

  // 블록 조작
  const renameBlock = (id, title) =>
    patchBlocks({ [id]: { ...state.blocks[id], title } });
  const toggleLock = (id) =>
    patchBlocks({
      [id]: { ...state.blocks[id], locked: !state.blocks[id]?.locked },
    });

  // ✅ 표 추가: 프리셋 선택 모달 열기
  const handleAddTable = () => setPickerOpen(true);

  // ✅ 프리셋 선택 결과로 표 생성
  const addTableByPreset = ({ schemaId, preset }) => {
    const bid = `block-table-${crypto.randomUUID()}`;
    // 블록 메타
    patchBlocks({
      [bid]: { title: preset.label, kind: "table", locked: false },
    });
    // 테이블 메타 + 스키마
    patchTables({
      [bid]: {
        schemaId, // 예: StockInLines
        role: preset.role, // "lines" | "generic"
        form: preset.form, // "PS" | "PO" | "GR" | "GI" | null
        asLines: preset.role === "lines",
        columns: preset.columns?.length
          ? preset.columns
          : DEFAULT_TABLE_COLUMNS,
        rows: [],
      },
    });

    // 레이아웃 배치
    const bottom = getBottomY(state.layout);
    const next = [
      ...state.layout,
      withConstraints({
        i: bid,
        x: 0,
        y: bottom,
        w: DEFAULT_W,
        h: Math.max(6, DEFAULT_H),
        minH: 4,
      }),
    ];
    setLayout(next);
    setPickerOpen(false);
  };

  // kind별 props
  const propsByKind = {
    title: {
      value: state.values.title,
      onChange: (v) => patchValues({ title: v }),
      onRename: renameBlock,
      onToggleLock: toggleLock,
      onRemove: () => {},
    },
    date: {
      value: state.values.dateStr,
      todayISO,
      onChange: (v) => patchValues({ dateStr: v }),
      onRename: renameBlock,
      onToggleLock: toggleLock,
      onRemove: () => {},
    },
    editor: {
      Editor,
      hiddenFileRef,
      editorProps: {
        onInit: (_, ed) => (editorRef.current = ed),
        tinymceScriptSrc: "/tinymce/tinymce.min.js",
        value: state.values.editorHtml,
        onEditorChange: (v) => patchValues({ editorHtml: v }),
        init: {
          license_key: "gpl",
          base_url: "/tinymce",
          height: 480,
          menubar: "file edit view insert format table tools",
          plugins:
            "link lists table image autoresize code charmap searchreplace visualblocks quickbars",
          toolbar:
            "undo redo | blocks | bold italic underline forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | table image | removeformat | code",
          toolbar_sticky: true,
          branding: false,
          readonly: alignMode ? 1 : 0,
          paste_data_images: true,
          file_picker_types: "image",
          file_picker_callback: (cb) => {
            if (!hiddenFileRef.current) return;
            hiddenFileRef.current.onchange = async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const src = await compressImage(file);
              cb(src, { title: file.name });
              e.target.value = "";
            };
            hiddenFileRef.current.click();
          },
          images_upload_handler: (blobInfo) =>
            new Promise((resolve) => {
              const r = new FileReader();
              r.onload = () => resolve(r.result);
              r.readAsDataURL(blobInfo.blob());
            }),
          invalid_elements: "script,iframe,object,embed,style,link,form",
        },
      },
      onRename: renameBlock,
      onToggleLock: toggleLock,
      onRemove: () => {},
    },
    photos: {
      inputProps: { onChange: handlePickPhotos },
      list: state.values.photos,
      onInsert: insertImageToEditor,
      onRemovePhoto: removePhoto,
      onRename: renameBlock,
      onToggleLock: toggleLock,
      onRemove: () => {},
    },
    note: {
      value: state.values.note,
      onChange: (v) => patchValues({ note: v }),
      onRename: renameBlock,
      onToggleLock: toggleLock,
      onRemove: () => {},
    },
    files: {
      files: state.values.files,
      onAttach: handleAttachFiles,
      onRemoveFile: removeFile,
      onRename: renameBlock,
      onToggleLock: toggleLock,
      onRemove: () => {},
    },
    table: {
      onAddRow: (bid) =>
        patchTables({
          [bid]: {
            ...(state.tables[bid] || {
              columns: DEFAULT_TABLE_COLUMNS,
              rows: [],
            }),
            rows: [...(state.tables[bid]?.rows || []), newRow()],
          },
        }),
      onRemoveRow: (bid, rid) => {
        const t = state.tables[bid];
        if (!t) return;
        patchTables({
          [bid]: { ...t, rows: t.rows.filter((r) => r._id !== rid) },
        });
      },
      onUpdateCell: (bid, rid, key, value) => {
        const t = state.tables[bid];
        if (!t) return;
        const cols = t.columns || DEFAULT_TABLE_COLUMNS;
        const rows = t.rows.map((r) =>
          r._id !== rid
            ? r
            : {
                ...r,
                [key]:
                  cols.find((c) => c.key === key)?.type === "number"
                    ? Number(value)
                    : value,
              }
        );
        patchTables({ [bid]: { ...t, rows } });
      },
      onRename: renameBlock,
      onToggleLock: toggleLock,
      onRemove: () => {},
    },
  };

  return (
    <div className="p-4 max-w-screen-2xl mx-auto pb-10">
      <FormTemplateManager
        params={{ module, formType, mode, scope, project }}
        getBody={getBody}
        onApply={(body) => applyBody(body)}
        onChangeTemplate={handleTemplateSelect}
        autoApplyOnMount={false}
      />

      {/* 상단 툴바: 좌측 정렬모드, 우측 아이콘/표추가 */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={toggleAlign}
          className={`px-3 py-2 rounded-xl border ${
            alignMode ? "bg-indigo-600 text-white" : "bg-white text-slate-900"
          }`}
        >
          {alignMode ? "정렬모드: ON" : "정렬모드: OFF"}
        </button>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={undo}
            className="px-2 py-2 rounded-xl border bg-white"
            aria-label="되돌리기"
            title="되돌리기"
          >
            <Undo2 size={18} />
          </button>
          <button
            type="button"
            onClick={redo}
            className="px-2 py-2 rounded-xl border bg-white"
            aria-label="다시하기"
            title="다시하기"
          >
            <Redo2 size={18} />
          </button>
          <button
            type="button"
            onClick={handleAddTable}
            className="px-3 py-2 rounded-xl border bg-white"
          >
            <span className="inline-flex items-center gap-1">
              <Plus size={18} />표 추가
            </span>
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        className="relative rounded-xl border border-slate-200"
        style={gridBackgroundStyle}
      >
        <GridCanvas
          layout={state.layout}
          setLayout={(l) => setLayout(l)}
          alignMode={alignMode}
          onApplyItem={(it) => ({
            ...it,
            static: !!state.blocks[it.i]?.locked,
          })}
        >
          {state.layout.map((item) => {
            const meta = state.blocks[item.i];
            if (!meta) return null;
            return (
              <div key={item.i}>
                <BlockRenderer
                  kind={meta.kind}
                  meta={meta}
                  alignMode={alignMode}
                  bid={item.i}
                  t={state.tables[item.i]}
                  {...propsByKind[meta.kind]}
                />
              </div>
            );
          })}
        </GridCanvas>
      </div>

      {/* ✅ 프리셋 선택 모달 */}
      {pickerOpen && (
        <TablePresetPicker
          formType={formType}
          onSelect={addTableByPreset}
          onClose={() => setPickerOpen(false)}
        />
      )}

      <FormContentManager
        params={{ module, formType, mode, scope, project }}
        getBody={getBody}
        onApply={(body) => applyBody(body)}
        template={currentTemplate}
      />
    </div>
  );
}
